import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { sectionRenderer } from '@theme/section-renderer';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, { productGrid: Element, productData: any, rating: any, description: string }>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    // Predictive-search cards intentionally use a lightweight card instead of
    // Horizon's full <product-card>. Allow those instances to provide the exact
    // product/variant URL directly while keeping the native product-card path.
    const productUrl = productLink?.href || this.dataset.productUrl;
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    let cached = this.#cachedContent.get(currentUrl);

    if (!cached) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          const productData = await this.fetchProductData(currentUrl);
          cached = {
            productGrid: /** @type {Element} */ (gridElement.cloneNode(true)),
            productData,
            rating: this.#extractAggregateRating(html),
            description: this.#extractProductDescription(html, productData),
          };
          this.#cachedContent.set(currentUrl, cached);
        }
      }
    }

    if (cached) {
      const freshContent = /** @type {Element} */ (cached.productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent, cached.productData, cached.rating, cached.description);
      this.#updateVariantPicker(cached.productGrid);
    }

    // When Quick View is launched from predictive search, finish preparing the
    // modal first, then close the search drawer. Closing predictive search resets
    // its results and removes this component, so doing it before the fetch would
    // abort the request from disconnectedCallback().
    await this.#closeParentSearchDialog();
    this.#openQuickAddModal();
  };

  /**
   * Closes the search drawer before opening Horizon's Quick View. Waiting for
   * DialogCloseEvent prevents two modal backdrops / scroll locks from overlapping.
   */
  async #closeParentSearchDialog() {
    const searchDialog = this.closest('dialog-component#search-modal');
    if (!(searchDialog instanceof DialogComponent)) return;

    const dialog = searchDialog.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve(undefined);
      };

      searchDialog.addEventListener(DialogCloseEvent.eventName, finish, { once: true });
      searchDialog.closeDialog();

      // Defensive fallback in case a customized dialog implementation does not
      // emit DialogCloseEvent after its closing animation.
      window.setTimeout(finish, 320);
    });
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const alloveSurface = dialogComponent.querySelector('.allove-quick-view');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    alloveSurface?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content.
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Fetches Shopify's product JSON endpoint so the Figma UI can stay fully dynamic.
   * @param {string} productPageUrl
   * @returns {Promise<any | null>}
   */
  async fetchProductData(productPageUrl) {
    const url = new URL(productPageUrl);
    url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    url.search = '';

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.warn('[quick-add] Product JSON unavailable; using Horizon fallback.', error);
      return null;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the quick-add modal and adds the Allove Figma presentation layer.
   * The original Horizon product grid stays mounted as an interaction bridge for
   * variant updates, add-to-cart, inventory, and third-party product blocks.
   * @param {Element} productGrid
   * @param {any} productData
   * @param {any} rating
   * @param {string} description
   */
  async updateQuickAddModal(productGrid, productData = null, rating = null, description = '') {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    // The enhanced Allove Quick View owns its selected variant locally. Do not
    // dispatch a native variant-picker change during modal setup, otherwise a
    // delayed Horizon section response can race the customer's first click and
    // reset the Quick View state. Keep Horizon's original sync for the fallback
    // non-enhanced modal only.
    if (!productData?.variants?.length) {
      this.#syncVariantSelection(modalContent);
    }

    if (productData?.variants?.length) {
      this.#enhanceQuickView(modalContent, productData, rating, description);
    } else {
      delete modalContent.dataset.alloveEnhanced;
      modalContent.querySelector(':scope > .allove-quick-view')?.remove();
    }
  }

  /**
   * Creates the Figma quick-view UI from Shopify product JSON while keeping the
   * original Horizon controls mounted invisibly underneath.
   * @param {HTMLElement} modalContent
   * @param {any} productData
   * @param {any} rating
   * @param {string} description
   */
  #enhanceQuickView(modalContent, productData, rating, description) {
    modalContent.dataset.alloveEnhanced = 'true';
    modalContent.querySelector(':scope > .allove-quick-view')?.remove();

    const optionNames = this.#getOptionNames(productData);
    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const selectedVariantId = new URL(this.productPageUrl).searchParams.get('variant');
    const initialVariant =
      variants.find((variant) => String(variant.id) === String(selectedVariantId)) ||
      variants.find((variant) => variant.available) ||
      variants[0];

    const state = {
      options: this.#getVariantOptions(initialVariant, optionNames.length),
      mediaIndex: 0,
    };

    const render = () => {
      const currentVariant =
        this.#findVariantForOptions(variants, state.options) || variants.find((variant) => variant.available) || variants[0];

      if (currentVariant) state.options = this.#getVariantOptions(currentVariant, optionNames.length);

      const nextRoot = this.#buildQuickViewRoot(
        modalContent,
        productData,
        optionNames,
        variants,
        state,
        currentVariant,
        rating,
        description,
        (optionIndex, optionValue) => {
          const candidateOptions = [...state.options];
          candidateOptions[optionIndex] = optionValue;

          let candidateVariant = this.#findVariantForOptions(variants, candidateOptions);
          if (!candidateVariant?.available) {
            candidateVariant = variants.find((variant) => {
              const options = this.#getVariantOptions(variant, optionNames.length);
              return variant.available && options[optionIndex] === optionValue;
            });
          }

          if (candidateVariant) {
            state.options = this.#getVariantOptions(candidateVariant, optionNames.length);
          } else {
            state.options = candidateOptions;
          }

          // Keep Quick View variant selection local while the dialog is open.
          // Dispatching a native variant-picker change here starts Horizon's
          // async section morphing flow, which can replace/reset controls inside
          // the dialog. The exact selected variant ID is sent to product-form
          // only when Add to Cart is clicked.
          render();
        }
      );

      const currentRoot = modalContent.querySelector(':scope > .allove-quick-view');
      if (currentRoot) currentRoot.replaceWith(nextRoot);
      else modalContent.appendChild(nextRoot);
    };

    // Do not mirror Quick View state into Horizon's native variant picker.
    // The native picker remains mounted only as a fallback/theme integration
    // bridge; Quick View itself is driven by product JSON until Add to Cart.
    render();
  }

  /** @returns {HTMLElement} */
  #buildQuickViewRoot(
    modalContent,
    productData,
    optionNames,
    variants,
    state,
    currentVariant,
    rating,
    description,
    onOptionSelect
  ) {
    const root = this.#createElement('div', 'allove-quick-view');
    root.setAttribute('data-quick-view-product-id', String(productData.id || ''));

    const media = this.#buildQuickViewMedia(productData, state);
    const details = this.#createElement('div', 'allove-quick-view__details');

    const metaRow = this.#createElement('div', 'allove-quick-view__meta-row');
    const eyebrow = this.#createElement(
      'div',
      'allove-quick-view__eyebrow',
      String(productData.type || productData.vendor || '').trim().toUpperCase()
    );
    if (eyebrow.textContent) metaRow.appendChild(eyebrow);

    if (rating?.value) {
      const ratingLink = this.#createElement('a', 'allove-quick-view__rating');
      ratingLink.href = `${this.productPageUrl.split('#')[0]}#reviews`;
      const star = this.#createElement('span', 'allove-quick-view__rating-star', '★');
      star.setAttribute('aria-hidden', 'true');
      const count = Number(rating.count || 0);
      const countLabel = count ? ` (${count}${count >= 100 ? '+' : ''} reviews)` : '';
      ratingLink.append(star, document.createTextNode(`${rating.value}${countLabel}`));
      metaRow.appendChild(ratingLink);
    }

    details.appendChild(metaRow);

    const intro = this.#createElement('div', 'allove-quick-view__intro');
    const title = this.#createElement('a', 'allove-quick-view__title', productData.title || this.dataset.productTitle || '');
    title.href = this.productPageUrl;
    intro.appendChild(title);
    if (description) intro.appendChild(this.#createElement('p', 'allove-quick-view__description', description));
    details.appendChild(intro);

    const optionsWrap = this.#createElement('div', 'allove-quick-view__options');
    const packIndex = this.#getPackOptionIndex(optionNames);

    optionNames.forEach((optionName, optionIndex) => {
      const values = this.#getOptionValues(variants, optionIndex, optionNames.length);
      if (!values.length) return;

      if (optionIndex === packIndex) {
        optionsWrap.appendChild(
          this.#buildPackOption(productData, optionName, optionIndex, values, variants, state, optionNames, onOptionSelect)
        );
      } else {
        optionsWrap.appendChild(
          this.#buildPillOption(optionName, optionIndex, values, variants, state, optionNames, onOptionSelect)
        );
      }
    });

    details.appendChild(optionsWrap);

    const actions = this.#createElement('div', 'allove-quick-view__actions');
    actions.appendChild(this.#buildPriceBlock(productData, currentVariant));

    const addButton = this.#createElement('button', 'allove-quick-view__add', 'add to cart');
    addButton.type = 'button';
    addButton.disabled = !currentVariant?.available;
    addButton.addEventListener('click', () => {
      if (!currentVariant?.id || !currentVariant.available) return;

      // Submit the exact variant shown in the Figma UI through Horizon's real
      // product-form component. This preserves line-item properties, selling
      // plans, cart section rendering and the normal CartAddEvent lifecycle.
      this.#addSelectedVariantToCart(modalContent, currentVariant.id);
    });
    actions.appendChild(addButton);

    const fullDetails = this.#createElement('a', 'allove-quick-view__full-details', 'view full details');
    fullDetails.href = this.productPageUrl;
    actions.appendChild(fullDetails);
    details.appendChild(actions);

    root.append(media, details);
    return root;
  }

  #buildQuickViewMedia(productData, state) {
    const media = this.#createElement('div', 'allove-quick-view__media');
    const imageUrls = (productData.images || [])
      .map((image) => (typeof image === 'string' ? image : image?.src))
      .filter(Boolean)
      .slice(0, 8);

    const main = this.#createElement('div', 'allove-quick-view__main-media');
    const mainImage = document.createElement('img');
    mainImage.alt = productData.title || '';
    mainImage.loading = 'eager';
    mainImage.src = imageUrls[state.mediaIndex] || productData.featured_image || '';
    main.appendChild(mainImage);
    media.appendChild(main);

    const thumbs = this.#createElement('div', 'allove-quick-view__thumbs');
    imageUrls.slice(0, 5).forEach((src, index) => {
      const button = this.#createElement(
        'button',
        `allove-quick-view__thumb${index === state.mediaIndex ? ' is-active' : ''}`
      );
      button.type = 'button';
      button.setAttribute('aria-label', `View product image ${index + 1}`);
      const image = document.createElement('img');
      image.src = src;
      image.alt = '';
      image.loading = index < 2 ? 'eager' : 'lazy';
      button.appendChild(image);
      button.addEventListener('click', () => {
        state.mediaIndex = index;
        mainImage.src = src;
        thumbs.querySelectorAll('.allove-quick-view__thumb').forEach((thumb, thumbIndex) => {
          thumb.classList.toggle('is-active', thumbIndex === index);
        });
      });
      thumbs.appendChild(button);
    });
    media.appendChild(thumbs);

    const strip = this.#createElement('div', 'allove-quick-view__mobile-strip');
    imageUrls.forEach((src, index) => {
      const imageWrap = this.#createElement('div', 'allove-quick-view__mobile-image');
      const image = document.createElement('img');
      image.src = src;
      image.alt = index === 0 ? productData.title || '' : '';
      image.loading = index < 3 ? 'eager' : 'lazy';
      imageWrap.appendChild(image);
      strip.appendChild(imageWrap);
    });
    media.appendChild(strip);

    return media;
  }

  #buildPillOption(optionName, optionIndex, values, variants, state, optionNames, onOptionSelect) {
    const section = this.#createElement('section', 'allove-quick-view__option-section');
    section.appendChild(this.#createElement('div', 'allove-quick-view__option-title', optionName));
    const list = this.#createElement('div', 'allove-quick-view__pills');

    values.forEach((value) => {
      const button = this.#createElement('button', 'allove-quick-view__pill', value);
      button.type = 'button';
      button.classList.toggle('is-selected', state.options[optionIndex] === value);
      button.disabled = !this.#hasAvailableOptionValue(variants, optionIndex, value, state.options, optionNames.length);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOptionSelect(optionIndex, value);
      });
      list.appendChild(button);
    });

    section.appendChild(list);
    return section;
  }

  #buildPackOption(productData, optionName, optionIndex, values, variants, state, optionNames, onOptionSelect) {
    const section = this.#createElement('section', 'allove-quick-view__option-section allove-quick-view__option-section--pack');
    section.appendChild(this.#createElement('div', 'allove-quick-view__option-title', optionName));
    const grid = this.#createElement('div', 'allove-quick-view__packs');

    values.forEach((value, valueIndex) => {
      const candidateOptions = [...state.options];
      candidateOptions[optionIndex] = value;
      const variant =
        this.#findVariantForOptions(variants, candidateOptions) ||
        variants.find((item) => this.#getVariantOptions(item, optionNames.length)[optionIndex] === value);

      const item = this.#createElement('div', 'allove-quick-view__pack-item');
      const tag = this.#createElement('span', 'allove-quick-view__pack-tag');
      if (valueIndex === values.length - 1 && values.length >= 3) {
        tag.textContent = 'BEST VALUE';
        tag.classList.add('is-best');
      } else if (valueIndex === 1 && values.length >= 3) {
        tag.textContent = 'RECOMMENDED';
        tag.classList.add('is-recommended');
      } else {
        tag.textContent = 'RECOMMENDED';
        tag.classList.add('is-placeholder');
      }
      item.appendChild(tag);

      const button = this.#createElement('button', 'allove-quick-view__pack-card');
      button.type = 'button';
      button.classList.toggle('is-selected', state.options[optionIndex] === value);
      button.disabled = !variant?.available;

      const unitLikeValue = /gumm|caps|tablet|softgel|sachet|serving|count|piece|pcs|ml|gram|gm\b/i.test(value);
      const label = unitLikeValue
        ? `${valueIndex + 1} ${valueIndex === 0 ? 'MONTH' : 'MONTHS'} PACK`
        : String(value).toUpperCase();
      button.appendChild(this.#createElement('span', 'allove-quick-view__pack-label', label));
      if (unitLikeValue) button.appendChild(this.#createElement('span', 'allove-quick-view__pack-detail', value));

      const priceRow = this.#createElement('span', 'allove-quick-view__pack-price');
      priceRow.appendChild(document.createTextNode(this.#formatMoney(variant?.price ?? productData.price)));
      if (variant?.compare_at_price > variant?.price) {
        priceRow.appendChild(
          this.#createElement('span', 'allove-quick-view__pack-compare', this.#formatMoney(variant.compare_at_price))
        );
      }
      button.appendChild(priceRow);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOptionSelect(optionIndex, value);
      });
      item.appendChild(button);

      const discount = this.#discountPercent(variant?.price, variant?.compare_at_price);
      const discountLabel = this.#createElement('span', 'allove-quick-view__pack-discount', discount ? `${discount}% OFF` : '');
      if (!discount) discountLabel.classList.add('is-placeholder');
      item.appendChild(discountLabel);
      grid.appendChild(item);
    });

    section.appendChild(grid);
    return section;
  }

  #buildPriceBlock(productData, variant) {
    const priceBlock = this.#createElement('div', 'allove-quick-view__price-block');
    const row = this.#createElement('div', 'allove-quick-view__price-row');
    const price = variant?.price ?? productData.price;
    const compareAt = variant?.compare_at_price ?? productData.compare_at_price;
    row.appendChild(this.#createElement('span', 'allove-quick-view__price', this.#formatMoney(price)));

    if (compareAt > price) {
      row.appendChild(this.#createElement('span', 'allove-quick-view__compare-price', this.#formatMoney(compareAt)));
      row.appendChild(
        this.#createElement('span', 'allove-quick-view__discount', `${this.#discountPercent(price, compareAt)}% OFF`)
      );
    }

    priceBlock.appendChild(row);
    priceBlock.appendChild(this.#createElement('span', 'allove-quick-view__tax-note', 'MRP Inc. all taxes'));
    return priceBlock;
  }

  #getOptionNames(productData) {
    const options = Array.isArray(productData.options) ? productData.options : [];
    if (options.length) return options.map((option) => (typeof option === 'string' ? option : option?.name)).filter(Boolean);

    const count = Math.max(
      0,
      ...((productData.variants || []).map((variant) => (Array.isArray(variant.options) ? variant.options.length : 0)))
    );
    return Array.from({ length: count }, (_, index) => `Option ${index + 1}`);
  }

  #getVariantOptions(variant, optionCount) {
    if (!variant) return Array(optionCount).fill('');
    if (Array.isArray(variant.options)) return variant.options.map(String);
    return Array.from({ length: optionCount }, (_, index) => String(variant[`option${index + 1}`] || ''));
  }

  #getOptionValues(variants, optionIndex, optionCount) {
    return [
      ...new Set(
        variants
          .map((variant) => this.#getVariantOptions(variant, optionCount)[optionIndex])
          .filter((value) => value && value !== 'Default Title')
      ),
    ];
  }

  #getPackOptionIndex(optionNames) {
    const explicit = optionNames.findIndex((name) => /pack|size|quantity|supply|month|count/i.test(name));
    if (explicit >= 0) return explicit;
    return optionNames.length > 1 ? 1 : -1;
  }

  #findVariantForOptions(variants, selectedOptions) {
    return variants.find((variant) => {
      const options = this.#getVariantOptions(variant, selectedOptions.length);
      return selectedOptions.every((value, index) => options[index] === value);
    });
  }

  #hasAvailableOptionValue(variants, optionIndex, value, _selectedOptions, optionCount) {
    return variants.some((variant) => {
      if (!variant.available) return false;
      const options = this.#getVariantOptions(variant, optionCount);
      return options[optionIndex] === value;
    });
  }

  /**
   * Mirrors the custom Figma controls into Horizon's real variant picker.
   * Horizon's variant-main-picker identifies radio groups by data-fieldset-index
   * (not data-option-name), while dropdowns keep the Shopify options[...] name.
   *
   * @param {HTMLElement} modalContent
   * @param {string[]} optionNames
   * @param {string[]} selectedOptions
   * @param {number | null} changedOptionIndex
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  #syncNativeOptions(modalContent, optionNames, selectedOptions, changedOptionIndex = null) {
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) return null;

    /** @type {Map<number, HTMLInputElement | HTMLSelectElement>} */
    const controls = new Map();

    optionNames.forEach((optionName, optionIndex) => {
      const selectedValue = selectedOptions[optionIndex];
      if (!selectedValue) return;

      const fieldset =
        variantPicker.querySelector(`fieldset[data-fieldset-index="${optionIndex}"]`) ||
        variantPicker.querySelectorAll('fieldset.variant-option')[optionIndex];

      const radio = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).find(
        (input) => input instanceof HTMLInputElement && input.value === selectedValue
      );

      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
        controls.set(optionIndex, radio);
        return;
      }

      const select = Array.from(variantPicker.querySelectorAll('select')).find(
        (element) =>
          element instanceof HTMLSelectElement &&
          (element.name === `options[${optionName}]` ||
            element.id.endsWith(`-${optionIndex}`)) &&
          Array.from(element.options).some((option) => option.value === selectedValue)
      );

      if (select instanceof HTMLSelectElement) {
        select.value = selectedValue;
        controls.set(optionIndex, select);
      }
    });

    // Fire exactly one native change event for the option the customer clicked.
    // variant-picker reads all currently checked controls when building its
    // option_values request, so this also supports multi-option products without
    // racing several section-rendering requests against each other.
    const changedControl = changedOptionIndex === null ? null : controls.get(changedOptionIndex) || null;
    changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
    return changedControl;
  }

  /**
   * Adds the exact selected variant via Horizon's product-form component and
   * opens the cart drawer after a successful CartAddEvent.
   * @param {HTMLElement} modalContent
   * @param {string | number} variantId
   */
  #addSelectedVariantToCart(modalContent, variantId) {
    const productForm = modalContent.querySelector('product-form-component');
    if (!(productForm instanceof HTMLElement)) return;

    const selectedVariantId = String(variantId);
    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      // Keep the native form truthful immediately, even if the variant section
      // request is still finishing in the background.
      variantInput.value = selectedVariantId;
    }

    const handleSuccessfulAdd = async (event) => {
      if (event.detail?.data?.didError) return;

      /*
       * The cart is already updated on Shopify at this point, but this theme keeps
       * the cart drawer inside the header section. Opening the drawer immediately
       * can therefore show the old header/cart markup until a full page refresh.
       *
       * Force every cart-items section to re-render from Shopify with cache disabled
       * BEFORE opening the drawer. This keeps the drawer products, totals, discounts
       * and empty/non-empty state in sync without refreshing the page.
       */
      const sectionIds = [
        ...new Set(
          Array.from(document.querySelectorAll('cart-items-component'))
            .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
            .filter(Boolean)
        ),
      ];

      if (sectionIds.length) {
        try {
          await Promise.all(
            sectionIds.map((sectionId) =>
              sectionRenderer.renderSection(sectionId, {
                cache: false,
                mode: 'full',
              })
            )
          );
        } catch (error) {
          // The normal cart:update listeners are still available as a fallback.
          console.warn('[quick-add] Cart drawer refresh failed; using cart event fallback.', error);
        }
      }

      const quickAddDialog = document.getElementById('quick-add-dialog');
      if (quickAddDialog instanceof QuickAddDialog) {
        quickAddDialog.closeDialog();
      }

      // Query the drawer AFTER the section refresh because a full section morph can
      // replace the original cart-drawer-component instance.
      requestAnimationFrame(() => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
      });
    };

    productForm.addEventListener(CartAddEvent.eventName, handleSuccessfulAdd, { once: true });

    if (typeof productForm.addVariantToCart === 'function') {
      productForm.addVariantToCart(selectedVariantId);
      return;
    }

    // Backward-compatible fallback if quick-add.js is deployed before the
    // accompanying product-form.js update.
    const form = productForm.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]')).find(
      (button) => !(button instanceof HTMLButtonElement || button instanceof HTMLInputElement) || !button.disabled
    );

    if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
      form.requestSubmit(submitButton);
    } else {
      form.requestSubmit();
    }
  }

  #extractAggregateRating(html) {
    const scripts = html.querySelectorAll('script[type="application/ld+json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent || 'null');
        const product = this.#findJsonLdProduct(data);
        const aggregate = product?.aggregateRating;
        if (aggregate?.ratingValue) {
          return {
            value: aggregate.ratingValue,
            count: aggregate.reviewCount || aggregate.ratingCount || 0,
          };
        }
      } catch (_error) {
        // Ignore unrelated or malformed JSON-LD blocks.
      }
    }
    return null;
  }

  #findJsonLdProduct(value) {
    if (!value) return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const match = this.#findJsonLdProduct(item);
        if (match) return match;
      }
      return null;
    }
    if (typeof value !== 'object') return null;

    const type = value['@type'];
    if (type === 'Product' || (Array.isArray(type) && type.includes('Product'))) return value;

    for (const nested of Object.values(value)) {
      const match = this.#findJsonLdProduct(nested);
      if (match) return match;
    }
    return null;
  }

  #extractProductDescription(html, productData) {
    const metaDescription = html.querySelector('meta[property="og:description"]')?.getAttribute('content')?.trim();
    if (metaDescription) return metaDescription;

    if (!productData?.description) return '';
    const holder = document.createElement('div');
    holder.innerHTML = productData.description;
    const firstParagraph = holder.querySelector('p')?.textContent?.trim();
    return firstParagraph || holder.textContent?.trim() || '';
  }

  #discountPercent(price, compareAt) {
    const current = Number(price || 0);
    const original = Number(compareAt || 0);
    if (!current || !original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'INR';
    const locale = document.documentElement.lang || 'en-IN';
    const hasDecimals = Math.round(amount) !== amount;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      })
        .format(amount)
        .replace(/\u00a0/g, ' ');
    } catch (_error) {
      return `${amount.toFixed(hasDecimals ? 2 : 0)}`;
    }
  }

  #createElement(tagName, className, text = '') {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== '') element.textContent = text;
    return element;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const alloveProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.allove-quick-view__title'));
    const alloveDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.allove-quick-view__full-details'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (alloveProductTitle) alloveProductTitle.href = anchorElement.href;
    if (alloveDetailsLink) alloveDetailsLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
